import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

import PanelTemplate from "../../../templates/Panel";
import PageHeader from "../../../components/PageHeader";
import Box from "../../../components/Box";
import Table from "../../../components/Table";
import Button from "../../../components/Button";
import FilterSimple from "../../../components/FilterSimple";
import Pagination from "../../../components/Pagination";
import SortHeader from "../../../components/SortHeader";
import { ModalDelete } from "../../../components/ModalDelete";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { hasPermission } from "../../../services/auth";
import { convertIntToMoney } from "../../../helpers/conversions";

import ModalEntry from "../ModalEntry";
import ModalSettle from "../ModalSettle";
import ModalSettleBatch from "../ModalSettleBatch";
import ModalFilter from "../ModalFilter";
import BillingCompanyFilter from "../BillingCompanyFilter";
import {
    getBillingCompanyLabel,
    getStoredBillingCompany,
    storeBillingCompany,
    useBillingCompanies,
} from "../billingCompany";
import {
    getEntryStatusLabel,
    getRemainingAmount,
    getSettledAmount,
    isSettleable,
} from "../helpers";

const entrySearchParams = [
    { name: 'Descrição', key: '[description]', operation: 'contain', type: 'text' },
];

// Colunas ordenáveis (SortHeader). "Pago" e "Em aberto" são derivadas das
// baixas — não têm coluna no banco para ordenar.
const indexMap = [
    { index: 'Vencimento', value: 'due_date' },
    { index: 'Descrição', value: 'description' },
    { index: 'Valor', value: 'amount' },
    { index: 'Status', value: 'status' },
];

const normalizePage = page => {
    const parsedPage = parseInt(page, 10);
    return Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;
};

const parseEntryListFilters = search => {
    const params = new URLSearchParams(search);

    return {
        page: normalizePage(params.get('page')),
        search: params.get('search') || '',
        status: params.get('status') || '',
        dueStart: params.get('due_start') || '',
        dueEnd: params.get('due_end') || '',
        amountMin: params.get('amount_min') || '',
        amountMax: params.get('amount_max') || '',
        categoryId: params.get('category') || '',
        costCenterId: params.get('cost_center') || '',
        // Ausente na URL significa "a que eu escolhi por último", não "todas":
        // semear aqui em vez de navegar depois evita um segundo paginate no
        // primeiro load.
        billingCompanyId: params.has('billing_company')
            ? (params.get('billing_company') || '')
            : getStoredBillingCompany(),
        // Filtro por pedido: quem chega da aba financeira do pedido vê só os
        // títulos dele. Não entra no modal de filtros — é um recorte que vem
        // de fora, e o aviso no topo da lista dá o caminho de volta.
        orderId: params.get('order_id') || '',
        orderCode: params.get('order_code') || '',
        paymentMethod: params.get('payment_method') || '',
        ordenationItem: params.get('sort') || 'due_date',
        orientation: params.get('orientation') || 'asc',
    };
};

const buildEntryApiQuery = (filters, type) => {
    const params = new URLSearchParams();

    params.set('type', type);
    if (filters.search.trim() !== '') params.set('search', filters.search.trim());
    if (filters.status) params.set('status', filters.status);
    if (filters.dueStart) params.set('due_date_start', filters.dueStart);
    if (filters.dueEnd) params.set('due_date_end', filters.dueEnd);
    if (filters.amountMin) params.set('amount_min', filters.amountMin);
    if (filters.amountMax) params.set('amount_max', filters.amountMax);
    if (filters.categoryId) params.set('finance_category_id', filters.categoryId);
    if (filters.costCenterId) params.set('finance_cost_center_id', filters.costCenterId);
    if (filters.billingCompanyId) params.set('billing_company_id', filters.billingCompanyId);
    if (filters.orderId) params.set('order_id', filters.orderId);
    if (filters.paymentMethod) params.set('payment_method_id', filters.paymentMethod);
    params.set('ordenation_item', filters.ordenationItem);
    params.set('orientation', filters.orientation);
    params.set('page', filters.page);

    return params.toString();
};

const buildEntryListSearch = filters => {
    const params = new URLSearchParams();

    if (filters.page > 1) params.set('page', filters.page);
    if (filters.search.trim() !== '') params.set('search', filters.search.trim());
    if (filters.status) params.set('status', filters.status);
    if (filters.dueStart) params.set('due_start', filters.dueStart);
    if (filters.dueEnd) params.set('due_end', filters.dueEnd);
    if (filters.amountMin) params.set('amount_min', filters.amountMin);
    if (filters.amountMax) params.set('amount_max', filters.amountMax);
    if (filters.categoryId) params.set('category', filters.categoryId);
    if (filters.costCenterId) params.set('cost_center', filters.costCenterId);
    if (filters.billingCompanyId) params.set('billing_company', filters.billingCompanyId);
    if (filters.orderId) params.set('order_id', filters.orderId);
    if (filters.orderCode) params.set('order_code', filters.orderCode);
    if (filters.paymentMethod) params.set('payment_method', filters.paymentMethod);
    if (filters.ordenationItem !== 'due_date') params.set('sort', filters.ordenationItem);
    if (filters.orientation !== 'asc') params.set('orientation', filters.orientation);

    return params.toString();
};

// `type` e `billingCompanyId` ficam de fora: o primeiro é dirigido pelas abas, o
// segundo tem seletor próprio no cabeçalho.
const countActiveEntryFilters = filters => (
    ['status', 'dueStart', 'dueEnd', 'amountMin', 'amountMax', 'categoryId', 'costCenterId', 'paymentMethod']
        .filter(key => filters[key] !== '' && filters[key] !== null).length
);

// Lista parametrizada pelo tipo: /finance/receivables (Contas a receber) e
// /finance/payables (Contas a pagar) renderizam este mesmo componente.
export const FinanceEntryList = ({ type }) => {
    const [loading, setLoading] = useState(true);

    const [entries, setEntries] = useState([]);
    const [data, setData] = useState({ header: [], rows: [] });

    const [categories, setCategories] = useState([]);
    const [costCenters, setCostCenters] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);

    const [showFilter, setShowFilter] = useState(false);
    const [showEntryModal, setShowEntryModal] = useState(false);
    const [entryToEdit, setEntryToEdit] = useState(null);
    const [entryToSettle, setEntryToSettle] = useState(null);
    const [entryToCancel, setEntryToCancel] = useState(null);
    const [showSettleBatch, setShowSettleBatch] = useState(false);
    const [canceling, setCanceling] = useState(false);

    const [selectedIds, setSelectedIds] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const entryFilters = useMemo(() => parseEntryListFilters(location.search), [location.search]);
    const activeFilterCount = useMemo(() => countActiveEntryFilters(entryFilters), [entryFilters]);

    const { billingCompanies, hasMultiple: hasMultipleBillingCompanies, defaultBillingCompanyId } = useBillingCompanies();
    const billingCompanyChecked = useRef(false);

    // Empresa guardada que não existe mais (excluída, ou desmarcada como de
    // faturamento) filtraria a lista para sempre vazia, sem nada na tela
    // explicando o porquê. Descarta e volta para Todas.
    useEffect(() => {
        if (billingCompanyChecked.current || billingCompanies.length === 0) return;
        billingCompanyChecked.current = true;

        const selected = entryFilters.billingCompanyId;
        if (!selected || billingCompanies.some(company => company.id === selected)) return;

        storeBillingCompany('');
        updateEntryListUrl({ billingCompanyId: '' });
    }, [billingCompanies]);

    const canManage = hasPermission('manage_finance');
    const entityLabel = type === 'receivable' ? 'receita' : 'despesa';
    const getPaidLabel = () => type === 'receivable' ? 'Recebido' : 'Pago';

    useEffect(() => {
        getSupportResources();
    }, []);

    useEffect(() => {
        getEntries();
    }, [location.search]);

    useEffect(() => {
        generateData();
    }, [entries, location.search]);

    const getSupportResources = () => {
        api.get(`/finance-category`).then(res => setCategories(res.data.resources)).catch(() => {});
        api.get(`/finance-cost-center`).then(res => setCostCenters(res.data.resources)).catch(() => {});
        api.get(`/finance-bank-account`).then(res => setBankAccounts(res.data.resources.filter(account => account.active))).catch(() => {});
    };

    const updateEntryListUrl = (changes, resetPage = true) => {
        const nextFilters = {
            ...entryFilters,
            ...changes,
            page: resetPage ? 1 : (changes.page || entryFilters.page),
        };
        const search = buildEntryListSearch(nextFilters);

        navigate({
            pathname: location.pathname,
            search: search ? `?${search}` : '',
        });
    };

    const getEntries = () => {
        setEntries([]);
        setSelectedIds([]);
        setLoading(true);

        api.get(`/finance-entry/paginate?${buildEntryApiQuery(entryFilters, type)}`).then(res => {
            setEntries(res.data.resources.data);
            setCurrentPage(res.data.resources.current_page);
            setLastPage(res.data.resources.last_page);
            setPerPage(res.data.resources.per_page);
            setTotalResults(res.data.resources.total);
            setHasNextPage(res.data.resources.next_page_url !== null);
            setHasPrevPage(res.data.resources.prev_page_url !== null);
        }).catch(renderError).then(() => setLoading(false));
    };

    const generateData = () => {
        // A coluna só entra quando a lista mistura CNPJs: filtrada numa empresa,
        // repetir o mesmo nome em toda linha é ruído.
        const showBillingCompany = hasMultipleBillingCompanies && !entryFilters.billingCompanyId;

        const header = ['Vencimento', 'Descrição'];
        if (showBillingCompany) header.push('Empresa');
        header.push('Valor', getPaidLabel(), 'Em aberto', 'Status');
        if (canManage) header.push('');

        const rows = entries.map(entry => {
            const overdue = isSettleable(entry) && moment(entry.due_date).isBefore(moment(), 'day');

            // Célula única de ações, presente em TODAS as linhas: o Table cria
            // um <td> por item de buttons, e linhas com contagens diferentes
            // de células desalinham as bordas.
            const buttons = canManage ? [
                <div className={`finance-row-actions`}>
                    {isSettleable(entry) &&
                        <Button type={`secondary`} size={`small`} text={`Baixar`} action={() => setEntryToSettle(entry)} />
                    }
                    {!isSettleable(entry) && (entry.settlements ?? []).length > 0 &&
                        <Button type={`secondary`} size={`small`} text={`Histórico`} action={() => setEntryToSettle(entry)} />
                    }
                    {entry.status === 'open' && (entry.settlements ?? []).length === 0 &&
                        <span title={`Cancelar título`}>
                            <Button type={`delete`} svg={`delete`} action={() => setEntryToCancel(entry)} />
                        </span>
                    }
                </div>
            ] : [];

            const cells = [
                <span className={overdue ? 'finance-overdue' : ''}>{moment(entry.due_date).format('DD/MM/YYYY')}</span>,
                <>
                    {entry.description}
                    {entry.finance_recurrence_id && <span className={`finance-recurrence-tag`} title={`Lançamento recorrente`}> ⟳</span>}
                    {entry.created_by_automation && <span className={`finance-automation-tag`} title={`Lançado por automação do financeiro`}>auto</span>}
                </>,
            ];

            if (showBillingCompany) {
                cells.push(
                    <span className={`finance-billing-company`}>
                        {entry.billing_company ? getBillingCompanyLabel(entry.billing_company) : '—'}
                    </span>
                );
            }

            cells.push(
                convertIntToMoney(entry.amount, true),
                convertIntToMoney(getSettledAmount(entry), true),
                convertIntToMoney(getRemainingAmount(entry), true),
                <span className={`finance-entry-status status-${entry.status}`}>{getEntryStatusLabel(entry.status)}</span>
            );

            return {
                data: cells,
                buttons,
                action: canManage ? () => { setEntryToEdit(entry); setShowEntryModal(true); } : undefined,
                id: canManage && isSettleable(entry) ? entry.id : null,
            };
        });

        setData({ header, rows });
    };

    const cancelEntry = () => {
        setCanceling(true);

        api.patch(`/finance-entry/${entryToCancel.id}/cancel`).then(() => {
            toast.success('Título cancelado');
            setEntryToCancel(null);
            getEntries();
        }).catch(renderError).then(() => setCanceling(false));
    };

    const handleSetBillingCompany = billingCompanyId => {
        storeBillingCompany(billingCompanyId);
        updateEntryListUrl({ billingCompanyId });
    };

    const handleSetFilter = (value, metadata) => {
        updateEntryListUrl({ search: metadata?.value ?? '' });
    };

    const handleSetPage = page => {
        updateEntryListUrl({ page }, false);
    };

    const handleOrdenation = (ordenationItem, orientation) => {
        updateEntryListUrl({ ordenationItem, orientation });
    };

    const refresh = () => {
        if (location.search === '') {
            getEntries();
        } else {
            navigate(location.pathname);
        }
    };

    const selectedEntries = entries.filter(entry => selectedIds.includes(entry.id));

    return (
        <>
            <ModalFilter
                show={showFilter}
                close={() => setShowFilter(false)}
                filters={entryFilters}
                setCustomFilters={changes => updateEntryListUrl(changes)}
                categories={categories}
                costCenters={costCenters}
            />
            <ModalEntry
                show={showEntryModal}
                close={() => { setShowEntryModal(false); setEntryToEdit(null); }}
                entry={entryToEdit}
                type={type}
                categories={categories}
                costCenters={costCenters}
                bankAccounts={bankAccounts}
                billingCompanies={billingCompanies}
                defaultBillingCompanyId={entryFilters.billingCompanyId || defaultBillingCompanyId}
                onSaved={refresh}
            />
            <ModalSettle
                show={entryToSettle !== null}
                close={() => setEntryToSettle(null)}
                entry={entryToSettle}
                bankAccounts={bankAccounts}
                onSaved={refresh}
            />
            <ModalSettleBatch
                show={showSettleBatch}
                close={() => setShowSettleBatch(false)}
                entries={selectedEntries}
                bankAccounts={bankAccounts}
                onSaved={refresh}
            />
            <ModalDelete
                show={entryToCancel !== null}
                text={<p>Tem certeza que deseja cancelar o título <strong>{entryToCancel?.description}</strong>?</p>}
                action={cancelEntry}
                loading={canceling}
                close={() => setEntryToCancel(null)}
            />

            <PanelTemplate id="finance-entry-list">
                <PageHeader
                    left={
                        <>
                            <FilterSimple
                                params={entrySearchParams}
                                filter={handleSetFilter}
                                externalValue={entryFilters.search}
                                externalOption={entrySearchParams[0].key}
                            />
                            <BillingCompanyFilter
                                billingCompanies={billingCompanies}
                                value={entryFilters.billingCompanyId}
                                change={handleSetBillingCompany}
                            />
                        </>
                    }
                    right={
                        <>
                            <Button type={`secondary`} size={`small`} svg={`filter`} text={`Filtrar`} action={() => setShowFilter(true)} badge={activeFilterCount} />
                            {canManage && selectedIds.length > 0 &&
                                <Button type={`secondary`} size={`small`} text={`Baixar ${selectedIds.length} selecionados`} action={() => setShowSettleBatch(true)} />
                            }
                            {canManage &&
                                <Button type={`primary`} size={`small`} svg={`plus-white`} text={`Nova ${entityLabel}`} action={() => { setEntryToEdit(null); setShowEntryModal(true); }} />
                            }
                        </>
                    }
                />

                {/* Recorte que veio de fora (a aba financeira do pedido): a
                    lista precisa dizer que está filtrada, senão parece que o
                    financeiro tem só estes títulos. */}
                {entryFilters.orderId &&
                    <div className={`finance-scope-notice`}>
                        <span>
                            Mostrando apenas os títulos do pedido
                            {entryFilters.orderCode ? <strong> #{entryFilters.orderCode}</strong> : null}.
                        </span>
                        <button onClick={() => updateEntryListUrl({ orderId: '', orderCode: '' })}>
                            Ver todos
                        </button>
                    </div>
                }

                <Box className={`no-padding with-pagination`}>
                    <Table
                        header={data.header}
                        rows={data.rows}
                        loading={loading}
                        checks={canManage}
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                        sortOrdenationAction={index => (
                            <SortHeader
                                index={index}
                                ordenationItem={entryFilters.ordenationItem}
                                orientation={entryFilters.orientation}
                                onSort={handleOrdenation}
                                indexMap={indexMap}
                            />
                        )}
                    />
                </Box>
                {!loading &&
                    <Pagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        perPage={perPage}
                        totalResults={totalResults}
                        hasNextPage={hasNextPage}
                        hasPrevPage={hasPrevPage}
                        setCurrentPage={handleSetPage}
                    />
                }
            </PanelTemplate>
        </>
    );
};

export default FinanceEntryList;
